"use client";

import React from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON, TERMS } from "@/lib/commonCopy";
interface SpecHighlight {
  label: Record<Lang, string>;
  val: string;
  desc: Record<Lang, string>;
}
const technicalHighlights: SpecHighlight[] = [{
  label: {
    en: "Woven Range",
    zh: "编织网范围",
    es: "Rango tejido",
    de: "Drahtgewebe-Bereich",
    fr: "Gamme tissée",
    pt: "Faixa tecida"
  },
  val: "1 - 635 Mesh",
  desc: {
    en: "Square weave, wire 0.020 - 2.0 mm",
    zh: "平纹/斜纹方孔网，丝径 0.020 - 2.0 mm",
    es: "Malla cuadrada, alambre 0.020 - 2.0 mm",
    de: "Quadratmasche, Draht 0.020 - 2.0 mm",
    fr: "Maille carrée, fil 0.020 - 2.0 mm",
    pt: "Malha quadrada, arame 0.020 - 2.0 mm"
  }
}, {
  label: {
    en: "Dutch Weave",
    zh: "荷兰编织密纹网",
    es: "Tejido Dutch",
    de: "Tressengewebe",
    fr: "Tissage hollandais",
    pt: "Trama holandesa"
  },
  val: "8x85 - 400x2800",
  desc: {
    en: "Nominal aperture ~1 - 310 µm",
    zh: "平纹及斜纹荷兰编织，名义精度 ~1 - 310 µm",
    es: "Abertura nominal ~1 - 310 µm",
    de: "Nenn-Maschenweite ~1 - 310 µm",
    fr: "Ouverture nominale ~1 - 310 µm",
    pt: "Abertura nominal ~1 - 310 µm"
  }
}, {
  label: {
    en: "Sintered Porous",
    zh: "烧结多层结构",
    es: "Malla sinterizada",
    de: "Sinterverbund",
    fr: "Structure frittée",
    pt: "Estrutura sinterizada"
  },
  val: "1 - 200 µm",
  desc: {
    en: "Multi-layer diffusion bonded laminate",
    zh: "多层扩散焊接复合结构，高强度抗反压",
    es: "Laminado unido por difusión multicapa",
    de: "Diffusionsverschweißtes Mehrlagenlaminat",
    fr: "Laminé multicouche soudé par diffusion",
    pt: "Laminado multicamadas ligado por difusão"
  }
}, {
  label: {
    en: "Alloy Provenance",
    zh: "合金牌号",
    es: "Aleaciones",
    de: "Legierungen",
    fr: "Nuances d'alliage",
    pt: "Ligas metálicas"
  },
  val: "304 / 316L / Alloys",
  desc: {
    en: "Mill-direct certified origin",
    zh: "钢厂直采，清晰批次与冶金来源",
    es: "Origen certificado directo de molino",
    de: "Zertifizierter Werksbezug mit Herkunftsnachweis",
    fr: "Origine aciérie certifiée et traçabilité",
    pt: "Origem certificada direta de usina"
  }
}];
export default function CapabilitiesHero() {
  const {
    lang
  } = useLanguage();
  return <section id="capabilitieshero" data-nav="light" className="relative overflow-hidden bg-background text-foreground border-b border-border mf-hero">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Main Hero Copy (7 cols on lg) */}
          <div className="lg:col-span-7 flex flex-col items-start">
            <Reveal>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="px-3 py-1 font-mono tracking-wide uppercase bg-muted text-foreground border-border mf-caption">
                  {pick(lang, {
                  en: "B2B Industrial Mesh Engineering & Supply",
                  zh: "全面的 B2B 工业丝网与过滤工程服务",
                  es: "Ingeniería y suministro de mallas industriales B2B",
                  de: "B2B-Industriedrahtgewebe & Filtrationsservice",
                  fr: "Ingénierie et fourniture de toiles industrielles B2B",
                  pt: "Engenharia e fornecimento de malhas industriais B2B"
                })}
                </Badge>
                <Badge variant="outline" className="bg-muted text-foreground px-2.5 py-1 font-mono mf-caption border border-border">
                  ISO 9044 / ASTM E2016
                </Badge>
              </div>

              <div>
                <h1 className="font-default mf-h1 text-foreground">
                  {pick(lang, {
                  en: "What Can MutualFilter Do for You?",
                  zh: "MutualFilter 能为您提供哪些工程支持与供应服务？",
                  es: "¿Qué puede hacer MutualFilter por su empresa?",
                  de: "Welche Leistungen bietet MutualFilter für Sie?",
                  fr: "Que peut faire MutualFilter pour votre entreprise ?",
                  pt: "O que a MutualFilter pode fazer por sua operação?"
                })}
                </h1>
                <p className="font-default mf-lead text-foreground/80 max-w-2xl mt-4">
                  {pick(lang, {
                  en: "We support industrial buyers, plant engineers, and OEMs with precision woven wire mesh specifications, rigorous material verification, and reliable global delivery across critical filtration applications.",
                  zh: "我们为工业采购商、工厂工程师和 OEM 客户提供定制编织丝网规格、严谨的材料验证和可靠的全球交付保障，覆盖关键过滤工艺环节。",
                  es: "Apoyamos a compradores industriales, ingenieros de planta y fabricantes OEM con especificaciones de malla de alambre tejida a medida, rigurosa verificación de materiales y entregas globales fiables en aplicaciones críticas.",
                  de: "Wir unterstützen Einkäufer, Betriebsingenieure und OEMs mit maßgeschneiderten Spezifikationen für Drahtgewebe, verlässlicher Werkstoffprüfung und termintreuer weltweiter Belieferung für anspruchsvolle Filtrationsaufgaben.",
                  fr: "Nous accompagnons acheteurs industriels, ingénieurs d'exploitation et équipementiers OEM avec des toiles métalliques tissées sur mesure, une vérification rigoureuse des matériaux et un approvisionnement mondial fiable pour la filtration critique.",
                  pt: "Apoiamos compradores industriais, engenheiros de planta e fabricantes OEM com especificações de malha de arame tecida sob medida, rigorosa verificação de materiais e entrega global confiável para filtração industrial."
                })}
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-4 mt-6">
                <Button asChild size="lg" className="font-semibold">
                  <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                    <span>{pick(lang, COMMON.requestQuote)}</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg">
                  <Link to="/TechnicalResourcesPage" className="inline-flex items-center gap-2">
                    <span>
                      {pick(lang, {
                      en: "Technical Guides",
                      zh: "技术指南",
                      es: "Guías técnicas",
                      de: "Technische Leitfäden",
                      fr: "Guides techniques",
                      pt: "Guias técnicos"
                    })}
                    </span>
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Technical Mesh Illustration / Visual Spec Card (5 cols on lg) */}
          <div className="lg:col-span-5">
            <Reveal delay={120}>
              <Card className="overflow-hidden gap-0 py-0 border border-border bg-card flex flex-col text-card-foreground">
                <div className="flex items-center justify-between border-b border-border p-4">
                  <div>
                    <p className="font-mono uppercase text-foreground/60 tracking-wider mf-caption">
                      {pick(lang, {
                      en: "Capabilities Portfolio",
                      zh: "工程能力组合",
                      es: "Portafolio de capacidades",
                      de: "Leistungsspektrum",
                      fr: "Portefeuille de compétences",
                      pt: "Portfólio de capacidades"
                    })}
                    </p>
                    <p className="font-default font-semibold text-foreground text-base">
                      {pick(lang, {
                      en: "Industrial Filtration & Precision Separation",
                      zh: "工业过滤与精密分离工程",
                      es: "Filtración industrial y separación de precisión",
                      de: "Industriefiltration & Präzisionstrennung",
                      fr: "Filtration industrielle & séparation de précision",
                      pt: "Filtração industrial e separação de precisão"
                    })}
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-muted text-foreground font-mono mf-caption">
                    ASTM E2016
                  </Badge>
                </div>

                {/* Technical Imagery with aspect-ratio container */}
                <div className="mf-card-media relative mf-ratio-hero w-full overflow-hidden">
                  <img src="https://mutualfilter.com/wp-content/uploads/2026/10/20-X-250-Mesh-0.3-X-0.22-mm-Dutch-Weave-Stainless-Steel-Wire-Mesh.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/capabilities_mesh_structure.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/capabilities_mesh_structure.png-wvc-sizes" alt={pick(lang, {
                  en: "Precision stainless steel woven wire mesh structure and aperture geometry",
                  zh: "精密不锈钢编织丝网微观结构与几何示意图",
                  es: "Estructura de malla de alambre de acero inoxidable de precisión y geometría de abertura",
                  de: "Präzisions-Drahtgewebestruktur aus Edelstahl und Maschengeometrie",
                  fr: "Structure de toile métallique en acier inoxydable de précision et géométrie d'ouverture",
                  pt: "Estrutura de malha de arame de aço inoxidável de precisão e geometria de abertura"
                })} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                  <div className="absolute bottom-2 left-2 bg-card px-2.5 py-1 rounded font-mono text-foreground border border-border mf-caption">
                    {pick(lang, {
                    en: "Filtration: 1 µm - 25 mm",
                    zh: "过滤精度范围: 1 µm - 25 mm",
                    es: "Filtración: 1 µm - 25 mm",
                    de: "Filtration: 1 µm - 25 mm",
                    fr: "Filtration : 1 µm - 25 mm",
                    pt: "Filtração: 1 µm - 25 mm"
                  })}
                  </div>
                </div>

                {/* Card Content Footer Row */}
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mf-caption">
                    <div className="flex items-center gap-1.5 text-muted-foreground mf-caption">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                      <span>
                        {pick(lang, {
                        en: "Rolls, stamped discs, cylinders & baskets",
                        zh: "支持原卷、冲压滤片、圆筒滤管与焊接滤篮",
                        es: "Rollos, discos estampados, cilindros y cestas",
                        de: "Rollen, Ronden, Zylinder und Filterkörbe",
                        fr: "Rouleaux, disques emboutis, cylindres et paniers",
                        pt: "Rolos, discos estampados, cilindros e cestos"
                      })}
                      </span>
                    </div>
                    <Link to="/OEMCustomMeshEngineeringPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0 mf-caption">
                      <span>{pick(lang, COMMON.oemCustom)}</span>
                      <ArrowRight className="size-3" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>

        {/* Spec Strip Below Grid */}
        <div className="mf-spec-strip">
          {technicalHighlights.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <div className="mf-spec-strip__label">
                {pick(lang, spec.label)}
              </div>
              <div className="mf-spec-strip__value font-mono">
                {spec.val}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}